import {
  Constraints,
  DynamicPage,
  DynamicPageVersion,
  DynamicPageVersionMetadata,
  Website,
} from '@superfunnel/types';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  setDoc,
  startAfter,
  Timestamp,
  UpdateData,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

export type CreatableWebsiteSectionType =
  | 'hero'
  | 'logos'
  | 'cta'
  | 'features'
  | 'testimonials'
  | 'faqs'
  | 'contactUs'
  | 'calendly'
  | 'footer';

export const dynamicPagesColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'campaigns').withConverter(
    emptyConverter<DynamicPage>(),
  );
};

export const dynamicPagesQuery = (accountId: string) => {
  return query(
    dynamicPagesColRef(accountId),
    where('status', 'in', ['active', 'disabled', 'draft']),
    orderBy('createdAt', 'desc'),
  );
};

export const createNewDynamicPageVariant = async (
  accountId: string,
  dynamicPageId: string,
  name: string,
  description: string | undefined,
  constraints: Constraints,
): Promise<{ variantId: string; websiteId: string }> => {
  const fn = httpsCallable<unknown, { variantId: string; websiteId: string }>(
    functions,
    'accounts/campaigns/addVariant',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    name,
    description,
    constraints,
  });

  return res.data;
};

export const updateWebsiteStatus = async (
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  status: 'active' | 'disabled',
): Promise<any> => {
  const websiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
    'websites',
    websiteId,
  );

  return updateDoc(websiteRef, { status });
};

export const suggestCampaignForAd = async (
  accountId: string,
  adId: string,
): Promise<{
  campaign: Partial<DynamicPage>;
  variants: Partial<Website>[];
}> => {
  const fn = httpsCallable<
    any,
    { campaign: Partial<DynamicPage>; variants: Partial<Website>[] }
  >(functions, 'accounts/campaigns/suggestCampaignForAd', { timeout: 600000 });

  const res = await fn({
    accountId,
    adId,
  });

  return res.data;
};

export const duplicateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ campaignId: string }> => {
  const fn = httpsCallable<any, { campaignId: string }>(
    functions,
    'accounts/campaigns/duplicateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
  });

  return res.data;
};

/**
 * Copy the development data from a source website to a target website.
 * Used when generating a variant based on an existing variant.
 */
export const copyWebsiteDevelopment = async (
  accountId: string,
  sourceCampaignId: string,
  sourceWebsiteId: string,
  targetCampaignId: string,
  targetWebsiteId: string,
): Promise<{ ok: boolean }> => {
  // Get source website document
  const sourceWebsiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    sourceCampaignId,
    'websites',
    sourceWebsiteId,
  ).withConverter(emptyConverter<Website>());

  const sourceWebsiteDoc = await getDoc(sourceWebsiteRef);
  const sourceWebsiteData = sourceWebsiteDoc.data();

  if (!sourceWebsiteData) {
    throw new Error('Source website not found');
  }

  if (!sourceWebsiteData.development) {
    throw new Error('Source website has no development data');
  }

  // Update target website with the source development data
  const targetWebsiteRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    targetCampaignId,
    'websites',
    targetWebsiteId,
  );

  await updateDoc(targetWebsiteRef, {
    development: sourceWebsiteData.development,
    changesToPublish: true,
  });

  return { ok: true };
};

export const updateDynamicPageHasPendingPublishChanges = async (
  accountId: string,
  dynamicPageId: string,
  hasChanges: boolean,
): Promise<void> => {
  const dynamicPageRef = doc(dynamicPagesColRef(accountId), dynamicPageId);

  return setDoc(
    dynamicPageRef,
    { hasChangesToPublish: hasChanges },
    { merge: true },
  );
};

export const updateDynamicPagePublishStatus = async (
  accountId: string,
  dynamicPageId: string,
): Promise<void> => {
  const dynamicPageRef = doc(dynamicPagesColRef(accountId), dynamicPageId);

  return setDoc(
    dynamicPageRef,
    {
      status: 'active',
      hasChangesToPublish: false,
    },
    { merge: true },
  );
};

export const getPrevDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
): Promise<QueryDocumentSnapshot<DynamicPageVersionMetadata>[]> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersionMetadata>());

  return getDocs(
    query(
      dynamicPageVersionsColRef,
      where('type', '==', 'dynamicPageVersion'),
      orderBy('createdAt', 'desc'),
      limit(1),
    ),
  ).then((querySnapshot) => querySnapshot.docs);
};

export const createDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
  dynamicPageVersion: DynamicPageVersion,
): Promise<{ id: string }> => {
  const fn = httpsCallable<unknown, { id: string }>(
    functions,
    'accounts/campaigns/versions/createDynamicPageVersion',
    { timeout: 120000 },
  );

  const res = await fn({
    ...dynamicPageVersion,
    accountId,
    dynamicPageId,
  });

  return res.data;
};

/**
 * Get the full contents of a dynamic page version from Cloud Storage.
 * Use this when displaying version details, not for listing versions.
 */
export const getDynamicPageVersionContents = async (
  accountId: string,
  dynamicPageId: string,
  versionId: string,
): Promise<DynamicPageVersion> => {
  const fn = httpsCallable<unknown, DynamicPageVersion>(
    functions,
    'accounts/campaigns/versions/getDynamicPageVersion',
    { timeout: 60000 },
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    versionId,
  });

  return res.data;
};

export const getDynamicPageVersions = async (
  accountId: string,
  dynamicPageId: string,
  _limit: number,
  last?: QueryDocumentSnapshot<DynamicPageVersionMetadata>,
): Promise<QueryDocumentSnapshot<DynamicPageVersionMetadata>[]> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const dynamicPageVersionsColRef = collection(
    dynamicPageDocRef,
    'versions',
  ).withConverter(emptyConverter<DynamicPageVersionMetadata>());

  return getDocs(
    query(
      dynamicPageVersionsColRef,
      where('type', '==', 'dynamicPageVersion'),
      orderBy('createdAt', 'desc'),
      ...(last ? [startAfter(last)] : []),
      limit(_limit),
    ),
  ).then((querySnapshot) => querySnapshot.docs);
};

export const updateDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
  update: UpdateData<
    Pick<
      DynamicPage,
      | 'name'
      | 'description'
      | 'config'
      | 'brand'
      | 'status'
      | 'tags'
      | 'connections'
    >
  >,
  mergeFields?: string[],
): Promise<any> => {
  const dynamicPageDocRef = doc(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
  );

  const res = await setDoc(dynamicPageDocRef, update, {
    merge: true,
    mergeFields,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res;
};

export const updateDynamicPageMetadata = async (
  accountId: string,
  dynamicPageId: string,
  update: { path?: string; alternatePathes?: string[] },
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/updateCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    update,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const deleteDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ success: boolean }> => {
  const fn = httpsCallable<any, { success: boolean }>(
    functions,
    'accounts/dynamicPages/deleteDynamicPage',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
  });

  return res.data;
};

export const unpublishDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<{ success: boolean }> => {
  const fn = httpsCallable<any, { success: boolean }>(
    functions,
    'accounts/campaigns/unpublishCampaign',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
  });

  return res.data;
};

export const getAllDynamicPagesLinks = async (
  accountId: string,
  dynamicPageIds: string[],
): Promise<string[]> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/linkReplacer/getAllLinks',
    { timeout: 600000 },
  );

  const res = await fn({
    accountId,
    campaignIds: dynamicPageIds,
  });

  return res.data;
};

export const createPreviewDynamicPageVersion = async (
  accountId: string,
  dynamicPageId: string,
): Promise<DynamicPageVersion> => {
  const fn = httpsCallable<any, DynamicPageVersion>(
    functions,
    'accounts/campaigns/versions/createPreviewDpVersion',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
  });

  return { ...res.data, createdAt: Timestamp.now() };
};

export const replaceAllDynamicPagesLinks = async (
  accountId: string,
  dynamicPageIds: string[],
  mappings: { link: string; toLink: string }[],
): Promise<any> => {
  const fn = httpsCallable<any, any>(
    functions,
    'accounts/campaigns/linkReplacer/replaceLinks',
    { timeout: 600000 },
  );

  const res = await fn({
    accountId,
    campaignIds: dynamicPageIds,
    mappings,
  });

  return res.data;
};
